import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '@core/providers/http/api.service';

export interface DocAnexo {
  id?: number;
  entidadeID?: number | string;
  tipoDocumento?: number | string;
  nomeOriginal?: string;
  contentType?: string;
  tamanho?: number;
  criadoEm?: string;
  criadoPor?: string;
  // acrescenta campos específicos do backend se existirem
}

export interface SaveDocAnexoRequest {
  entidadeID: number | string;
  tipoDocumento: number | string;
  ficheiro?: File | Blob; // preferível
  file?: File | Blob; // alias aceitável
  descricao?: string;
  nomeOriginal?: string;
  contentType?: string;
  [key: string]: any; // flexível a novos campos
}

@Injectable({
  providedIn: 'root',
})
export class DocAnexoService {
  private readonly path = 'bolsa';
  private readonly docAnexoPath = 'DocAnexo';

  constructor(private api: ApiService) {}

  /**
   * GET /DocAnexo/GetAll
   * Suporta filtro por EntidadeID e TipoDocumento via querystring.
   */
  list(params?: { [k: string]: any }): Observable<{ data: DocAnexo[] } | any> {
    const httpParams = params
      ? new HttpParams({ fromObject: params })
      : new HttpParams();

    return this.api
      .get<{ data: DocAnexo[] }>(
        `${this.path}/${this.docAnexoPath}/GetAll`,
        httpParams
      )
      .pipe(map((res) => res));
  }

  /**
   * POST /DocAnexo/Save
   * Aceita FormData diretamente, ou um objeto SaveDocAnexoRequest (converte para FormData).
   * Detecta automaticamente o ficheiro em `ficheiro` ou `file`.
   */
  save(payload: FormData | SaveDocAnexoRequest): Observable<any> {
    const body =
      payload instanceof FormData ? payload : this.buildFormData(payload);

    return this.api
      .post<any>(`${this.path}/${this.docAnexoPath}/Save`, body)
      .pipe(map((res) => res));
  }

  /**
   * GET /DocAnexo/GetFicheiro/{EntidadeID}/{TipoDocumento}
   * Retorna o ficheiro (Blob) para download/preview.
   */
  /**
   * GET /DocAnexo/GetFicheiro/{EntidadeID}/{TipoDocumento}
   * Retorna o ficheiro (Blob) para download/preview.
   */
  getFicheiro(
    entidadeID: number | string,
    tipoDocumento: number | string
  ): Observable<Blob> {
    return this.api.download(
      `${this.path}/${this.docAnexoPath}/GetFicheiro/${entidadeID}/${tipoDocumento}`
    );
  }

  getFicheiroById(id: number): Observable<Blob> {
    return this.api.download(
      `${this.path}/${this.docAnexoPath}/GetFicheiro/${id}`
    );
  }

  // ========= Helpers =========

  private buildFormData(req: SaveDocAnexoRequest): FormData {
    const fd = new FormData();

    // campos obrigatórios
    fd.append('EntidadeID', String(req.entidadeID));
    fd.append('TipoDocumento', String(req.tipoDocumento));

    // ficheiro: aceita ficheiro em `ficheiro` ou `file`
    const file = (req.ficheiro ?? req.file) as File | Blob | undefined;
    if (file) {
      // nome de ficheiro: usa nomeOriginal se fornecido, caso contrário tenta file.name
      const filename =
        req.nomeOriginal ||
        (file instanceof File ? file.name : `doc-${Date.now()}.bin`);
      fd.append('Ficheiro', file, filename);
    }

    // opcionais comuns
    if (req.descricao != null) fd.append('Descricao', String(req.descricao));
    if (req.nomeOriginal != null)
      fd.append('NomeOriginal', String(req.nomeOriginal));
    if (req.contentType != null)
      fd.append('ContentType', String(req.contentType));

    // quaisquer campos extra
    Object.entries(req)
      .filter(
        ([key]) =>
          ![
            'entidadeID',
            'tipoDocumento',
            'ficheiro',
            'file',
            'descricao',
            'nomeOriginal',
            'contentType',
          ].includes(key)
      )
      .forEach(([k, v]) => {
        if (v !== undefined && v !== null) fd.append(k, String(v));
      });

    return fd;
  }

  delete(id: number): Observable<any> {
    return this.api
      .delete<any>(`${this.path}/${this.docAnexoPath}/${id}`)
      .pipe(map((res) => res));
  }
}
